<template>
    <div>
        <div class="mystery-box-container" v-if="!showConfirmationPopUp && !showResultScreen">
            <div class="model-header d-flex align-center justify-between">
                <div>
                    <span>Exclusive Drop</span>
                    <h2>Mystery Box</h2>
                </div>
                <div class="closeButton">
                    <app-icon size="15" 
                        icon="close"
                        :clickable="true"
                        @click="handleClose"
                    ></app-icon>
                </div>
            </div>
            <!-- CONTENT -->
            <div v-if="data" key="content" class="model-body-content fade-in">
                <!-- HEADER -->
                <div class="header text-center">
                    <div class="glow-circle">
                        <mystery-box-icon />
                    </div>
                    <h2 class="title"> {{ $t('pages.user.setting.creator.mystery_box.title') }} </h2>
        
                    <p v-if="data.description" class="description mt-2">
                    {{ data.description }}
                    </p>
                </div>

                <!-- PRICE + REMAINING -->
                 <v-row class="price-remaining-items mt-4">
                    <v-col cols="6" class="price-remaining-item">
                        <div class="stat-item">
                                <strong class="price">{{ currency }}{{ data.price }}</strong>
                            <label> {{ $t('pages.user.setting.creator.mystery_box.title') }} </label>
                        </div>
                    </v-col>
                    <v-col cols="6" class="price-remaining-item">
                         <div class="stat-item">
                             <strong :color="remaining <= 5 ? 'error' : 'success'">
                                 {{ remaining }}
                             </strong>
                             <label> {{ $t('pages.user.setting.creator.mystery_box.remaining_boxes') }} </label>
                         </div>
                    </v-col>
                 </v-row>

                <!-- REWARDS -->
                <div class="rewards-section">
                   
                    <h3 class="section-title mb-4"> 
                        <possible-reward-icon /> 
                        {{ $t('pages.user.setting.creator.mystery_box.possible_rewards') }}
                    </h3>
                    <div class="rewards-grid">
                        <div v-for="(reward, i) in data.rewards" :key="i" class="reward-card" v-if="reward.title">
                            <div class="reward-card-inner">
                                <span class="rewards-icon">
                                    <mystery-reward-icon />
                                </span>            
                                <p class="reward-text mt-2"> {{ reward.title || "Blank Reward" }} </p>
                            </div>
                        </div>
                    </div>
                </div>
        
                <!-- OPEN BUTTON -->
                <div v-if="!result" class="open-box-wrapper">
                    <v-btn x-large color="primary" dark block class="open-box-btn" :loading="opening" :disabled="insufficientFunds || remaining === 0" @click="confirmationPopUp" >
                        {{ $t('pages.user.setting.creator.mystery_box.open_box') }} {{ currency }}{{ data.price }}
                    </v-btn>
        
                    <p v-if="insufficientFunds" class="error-msg text-center">
                    Insufficient balance. Need {{ currency }}{{ data.price }}
                    </p>
        
                    <p v-if="remaining === 0" class="disabled-msg text-center">
                    All boxes are opened!
                    </p>
                </div>
            </div>

            <!-- NO ACTIVE BOX -->
            <div v-else class="empty-state">
                <v-icon size="110" color="grey lighten-1">mdi-gift-off-outline</v-icon>
                <h3 class="empty-title">No Mystery Box Available</h3>
                <p class="empty-desc">This creator has not activated any Mystery Box yet.</p>
            </div>
        </div>

        <mystery-box-confirmation-dialog
            :price="data.price"
            v-if="showConfirmationPopUp"
            @open-box="openBox"
            @close="showConfirmationPopUp = false"
         />
    </div>
</template>

<script>
import { mapState, mapActions, mapMutations } from "vuex";
import MysteryBoxIcon from "@/assets/images/icons/open_mystery_box.svg?inline";
import MysteryRewardIcon from "@/assets/images/icons/mystery_reward.svg?inline";
import PossibleRewardIcon from "@/assets/images/icons/possible_rewards.svg?inline";
import MysteryBoxConfirmationDialog from "@/components/app/dialogs/MysteryBoxConfirmationDialog";

export default {
  name: "MysteryBoxDialog",
  components: { MysteryBoxIcon, MysteryRewardIcon, PossibleRewardIcon, MysteryBoxConfirmationDialog },

  props: {
    modelId: {
      type: [Number, String],
      required: true,
    },
    currency: {
      type: String,
      default: "$",
    },
    data: {
        type: Object,
        required: true
    },
    requestPrice: {
        type: Boolean,
        default:false
    }
  },

  data: () => ({
    useDummyData: false,
    loading: false,
    opening: false,
    claiming: false,
    result: null,
    won: false,
    rewardTitle: null,
    showConfirmationPopUp: false,
    showResultScreen: false,
  }),

  computed: {
    ...mapState({
      walletBalance: (state) => state.user.user?.data?.balance ?? 0
    }),

    remaining() {
      if (!this.data) return 0;
      return this.data.total_boxes - (this.data.sold_boxes || 0);
    },

    insufficientFunds() {
      return this.walletBalance < this.data?.price;
    },
  },

  watch: {
    modelId: {
      immediate: true,
      handler() {
        if (this.useDummyData) {
          this.loadDummyData();
        } 
      },
    },
  },

  methods: {
    ...mapMutations(["showSnackBar"]),
    ...mapActions(["deductFromWallet", "openMysteryBox"]),

    // DUMMY MODE — instant preview
    loadDummyData() {
      this.loading = true;
      setTimeout(() => {
        this.data = { ...this.dummyBox };
        this.loading = false;
      }, 800);
    },

    confirmationPopUp() {
        this.showConfirmationPopUp = true;
    },  
    async openBox() {
        if (this.insufficientFunds || this.remaining === 0) return;
        
        this.opening = true;

        const payload = { box_id: this.data.id, user_id: this.modelId, price: this.data.price };

        try {
            const res = await this.openMysteryBox({ model: payload });

            this.won = res.won;
            this.rewardTitle = res.reward;
            this.result = this.won
                ? this.rewardTitle
                : "You won nothing this time! Better luck for next time.";

            this.showConfirmationPopUp = false
            this.data.sold_boxes = res.sold_boxes;
            this.$emit("reward-box", { won: res.won, result: this.result, reward_title: this.rewardTitle, box_id: this.data.id, prize_requested_at: this.data.prize_requested_at } );
            // this.showResultScreen = true;
            // this.showSnackBar(this.result);
        } catch (err) {
            console.error(err);
            this.showSnackBar("Something went wrong!");
        }

        this.opening = false;
    },


    handleClose() {
        this.$emit("close");
        this.result = null;
        this.won = false;
        this.rewardTitle = null;
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/sass/modules/_variables";

.mystery-box-container { min-height: 100%; position: absolute; top: 0px; left: 0px; background: black; box-shadow: 0 10px 35px rgba(0,0,0,0.12); width: 100%;}
.model-header { padding: 18px 24px; border-bottom: 1px solid rgba(255, 255, 255, 0.15); }
.model-header { justify-content: space-between; }
.model-header span { display: inline-block; font-weight: 400; font-size: 14px; line-height: 16px; letter-spacing: 0px; color: rgba(255, 255, 255, 0.7); margin-bottom: 8px; }
.model-header h2 { font-weight: 700; font-size: 20px; line-height: 18px; letter-spacing: 0px; text-transform: capitalize; }
.model-header .closeButton { height: 38px; width: 38px; display: flex; align-items: center; justify-content: center; border-radius: 8px; color: rgba(149, 149, 150, 1); background: rgba(24, 24, 27, 0.5); border: 1px solid rgba(39, 39, 42, 1); } 
.model-body-content { padding: 24px; }
.model-body-content .header .glow-circle { display: inline-block; padding: 10px 12px; margin-bottom: 0px; }
// .model-body-content .header .glow-circle svg {  }
.model-body-content .header h2.title { font-size: 32px !important; font-weight: 800; line-height: 45px !important; letter-spacing: 0px; text-align: center; text-transform: capitalize; margin-bottom: 0px; }
.model-body-content .header .description { color: rgba(255, 255, 255, 0.54); font-weight: 500; font-size: 16px; line-height: 26px; letter-spacing: 0px; text-align: center; }
.model-body-content .price-remaining-items { margin: 0 -3px 34px; }
.model-body-content .price-remaining-item { padding: 0 3px; }
.model-body-content .price-remaining-items .stat-item { display: flex; flex-direction: column; align-items: center; justify-content: center; border: 1px solid rgba(85, 85, 85, 1); border-radius: 14px; padding: 22px 12px; }
.model-body-content .price-remaining-items .stat-item strong { background-image: linear-gradient(354.8deg, #FF7805 4.66%, #FFA702 83.25%); color: transparent; -webkit-background-clip: text; background-clip: text; font-weight: 800; font-size: 28px; line-height: 32px; letter-spacing: -1px; text-align: center; text-transform: capitalize; margin-bottom: 8px; }
.model-body-content .price-remaining-items .stat-item label { color: rgba(255, 255, 255, 0.7); font-weight: 500; font-size: 16px; line-height: 22.75px; letter-spacing: -0.5px; text-align: center; }

.mystery-box-container .rewards-section { color: rgba(255, 255, 255, 1); font-weight: 700;font-size: 20px;line-height: 18px;letter-spacing: 0px; margin-bottom: 34px; }
.mystery-box-container .rewards-section .rewards-grid { display: flex; flex-wrap: wrap; margin: 0 -4px; }
.mystery-box-container .rewards-section .rewards-grid .reward-card { width: 33.33%; padding: 0 4px; margin-bottom: 8px; }
.mystery-box-container .rewards-section .rewards-grid .reward-card .reward-card-inner { background: rgba(31, 31, 31, 1); border: 1px solid rgba(48, 48, 48, 1); border-radius: 14px; padding: 14px 12px; }
.mystery-box-container .rewards-section .reward-card-inner .rewards-icon { display: flex; align-items: center; justify-content: center; height: 32px; width: 32px; border-radius: 8px; background: linear-gradient(354.8deg, #FF7805 4.66%, #FFA702 83.25%); }
.mystery-box-container .rewards-section .reward-card-inner .reward-text { color: rgba(255, 255, 255, 1); font-weight: 700; font-size: 12px; line-height: 18px; letter-spacing: 0px; margin-bottom: 0; }

</style>
